import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Box,
  Container,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from '@mui/material';
import {
  LogoutOutlined,
  SyncAltOutlined,
  RecentActors,
  ExitToApp,
  RepeatOnRounded,
  PersonPinRounded,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { applicantPage, UserPanel } from './UserPanel';
import { useNavigate } from 'react-router-dom';
import type { DialogDetails, NavigationItem, SettingsItem } from './types';
import { APP_HEADER, DIALOG_TEXT } from './lang';
import Logo from './assets/gs-logo.png';
import PolyULogo from './assets/polyu-logo-3x.png';
import {
  DelegatePanel,
  IconButton,
  ROLES_MODULE,
  GS_ROLES_MODULE,
  RoleSwitchBar,
  handleRoleNavigation,
} from '../';
import { Dialog } from '../Dialog';
import { TokenType, useAuth } from '../../contexts';

type AppHeaderProps = {
  navigationItems: NavigationItem[];
  settingsItems: SettingsItem[];
};

// default login timeout, in milliseconds
const MAX_LOGOUT_TIME = parseInt(
  process.env.REACT_APP_MAX_LOGOUT_TIME ?? '5400000',
);

const delegationModeOn = process.env.REACT_APP_DELEGATION_ON === 'true';

export const AppHeader: React.FunctionComponent<AppHeaderProps> = (
  props: AppHeaderProps,
) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogDetails, setDialogDetails] = useState<DialogDetails>({
    title: '',
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [anchorElRole, setAnchorElRole] = useState<null | HTMLElement>(null);

  const { logout, user, role: currentRole } = useAuth();

  const hasLogin = user !== null;
  const isApplicant = user?.tokenType === TokenType.APPLICANT;

  const isGuest = ![TokenType.APPLICANT, TokenType.INTERNAL].includes(
    user?.tokenType ?? TokenType.GUEST,
  );
  const isStaff = user?.tokenType === TokenType.INTERNAL;

  const isGSMember = Object.values(GS_ROLES_MODULE).includes(
    currentRole as GS_ROLES_MODULE,
  );

  const { delegation = [] } = user ?? {};
  const tokens = localStorage.getItem('tokens');

  useEffect(() => {
    const lastActivityKey = 'lastActivityTime';
    const checkInterval = 1000; // check every second

    const resetIdleTimer = (): void => {
      localStorage.setItem(lastActivityKey, Date.now().toString());
    };

    const handleUserActivity = (): void => {
      resetIdleTimer();
    };

    const checkIdleTime = (): void => {
      const lastActivityTime = parseInt(
        localStorage.getItem(lastActivityKey) ?? '0',
        10,
      );
      if (Date.now() - lastActivityTime > MAX_LOGOUT_TIME) {
        localStorage.removeItem('tokens');
        logout();
        setDialogDetails(dialogMappings[DIALOG_TEXT.IDLE_LONG.KEY]);
        setDialogOpen(true);
      }
    };

    if (typeof tokens === 'string' && tokens.length !== 0) {
      window.addEventListener('mousemove', handleUserActivity);
      window.addEventListener('keypress', handleUserActivity);
      window.addEventListener('scroll', handleUserActivity);

      resetIdleTimer();

      const intervalId = setInterval(checkIdleTime, checkInterval);

      return () => {
        window.removeEventListener('mousemove', handleUserActivity);
        window.removeEventListener('keypress', handleUserActivity);
        window.removeEventListener('scroll', handleUserActivity);
        clearInterval(intervalId);
      };
    }
  }, [logout, tokens]);

  const handleOpenRoleMenu = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorElRole(event.currentTarget);
  };

  const handleCloseRoleMenu = (): void => {
    setAnchorElRole(null);
  };

  const handleOpenDelegation = (
    event: React.MouseEvent<HTMLButtonElement>,
  ): void => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseDelegation = (): void => {
    setAnchorEl(null);
  };

  const handleLogoutConfirm = (): void => {
    logout();
    setDialogOpen(false);
  };

  const handleNavHome = (): void => {
    if (isGuest) return;
    if (isApplicant) window.location.href = '/applications';
    if (currentRole !== null) handleRoleNavigation(currentRole);
  };

  const handleExitDelegationConfirm = (): void => {
    localStorage.removeItem('delegateToken');
    if (currentRole === ROLES_MODULE.SUPERVISOR) location.reload();
    // back to original role's homepage
    // currentRole might be null
    else if (currentRole != null) handleRoleNavigation(currentRole);

    setDialogOpen(false);
  };

  const handleToDelegate =
    (delegatorNetId: string, delegatorDept: string) => () => {
      const delegatorBase64 = btoa(delegatorNetId + ',' + delegatorDept);
      localStorage.setItem('delegateToken', delegatorBase64);
      if (currentRole !== ROLES_MODULE.SUPERVISOR)
        navigate('/internal/nominations');

      location.reload();
    };

  const hideUserInfo = (isGuest: boolean): boolean => {
    // matching any one of them will set "hide" to "true"
    const shouldHidePanelRoutes = [
      '/*',
      '/',
      '/login',
      '/internal/login',
      '/signup',
      '/forgot-password',
      '/reset-password',
      '/signup-successful',
      '/referee-report',
      /^\/referee-report(\?.*|\/.*)?$/, // matches any route starting with /referee-report, with or without query parameters
    ];

    // 1. route-level checking
    const isRouteHide = shouldHidePanelRoutes.some(route =>
      typeof route === 'string'
        ? route === window.location.pathname
        : route.test(window.location.pathname),
    );

    // 2. user-level checking, hide user info from guest user

    return isRouteHide || isGuest;
  };

  const dialogMappings: Record<string, DialogDetails> = {
    [DIALOG_TEXT.LOGOUT.KEY]: {
      title: DIALOG_TEXT.LOGOUT.TITLE,
      content: DIALOG_TEXT.LOGOUT.CONTENT,
      confirmAction: 'Confirm',
      cancelAction: 'Cancel',
      handleDialogConfirmAction: handleLogoutConfirm,
    },
    [DIALOG_TEXT.EXIT_DELEGATION.KEY]: {
      title: DIALOG_TEXT.EXIT_DELEGATION.TITLE,
      content: DIALOG_TEXT.EXIT_DELEGATION.CONTENT,
      confirmAction: 'Confirm',
      cancelAction: 'Cancel',
      handleDialogConfirmAction: handleExitDelegationConfirm,
    },
    [DIALOG_TEXT.IDLE_SHORT.KEY]: {
      title: DIALOG_TEXT.IDLE_SHORT.TITLE,
      content: DIALOG_TEXT.IDLE_SHORT.CONTENT,
      cancelAction: 'Got it',
    },
    [DIALOG_TEXT.IDLE_LONG.KEY]: {
      title: DIALOG_TEXT.IDLE_LONG.TITLE,
      content: DIALOG_TEXT.IDLE_LONG.CONTENT,
      confirmAction: 'Got it',
      handleDialogConfirmAction: () => {
        setDialogOpen(false);
      },
    },
  };

  //  academic staff who is not supervisor can also delegate supervisor to do nomination
  const showDelegation = Boolean(delegation.length) && delegationModeOn;

  const openDelegationList = anchorEl !== null;

  const delegateMode = user?.sub !== user?.userInfo?.netId && showDelegation;
  const isStaffViewApplicant = isStaff && applicantPage();

  return (
    <div style={{ height: '5rem' }} className="app-header">
      <AppBar
        style={{
          zIndex: 1200,
          background: 'white',
          color: theme.palette.primary.main,
          paddingTop: '0.5rem',
          paddingBottom: '0.5rem',
          marginBottom: '1rem',
          position: 'fixed',
        }}
      >
        <Dialog
          openDialog={dialogOpen}
          dialogTitle={dialogDetails.title}
          dialogAction={dialogDetails.confirmAction}
          cancelAction={dialogDetails.cancelAction ?? undefined}
          onDialogAction={dialogDetails.handleDialogConfirmAction}
          onDialogClose={() => {
            setDialogOpen(false);
          }}
        >
          <Typography margin={'0rem 1.5rem 2rem'}>
            {dialogDetails.content}
          </Typography>
        </Dialog>

        <Container maxWidth={false} sx={{ alignItems: 'baseline' }}>
          <Toolbar disableGutters className="flex justify-between">
            <div
              className={`flex ${!isGuest && 'cursor-pointer'} justify-between items-center w-[31rem]`}
              onClick={handleNavHome}
              title="Home"
            >
              <img className="max-w-[16rem]" src={PolyULogo} alt="PolyU Logo" />
              <img
                className="max-w-[14rem]"
                style={{ marginTop: '-0.6rem' }}
                src={Logo}
                alt="Graduate School Logo"
              />
            </div>

            <div>
              {hasLogin && !hideUserInfo(isGuest) && (
                <div className={delegateMode ? 'grid grid-rows-2' : ''}>
                  <Box sx={{ flexGrow: 0 }} className="flex flex-row">
                    <UserPanel
                      hidden={hideUserInfo(isGuest)}
                      isStaffViewApplicant={isStaffViewApplicant}
                    />

                    {/* hide mockuser when staff don't have the GS right */}
                    {isGSMember && !isStaffViewApplicant && (
                      <>
                        <IconButton
                          title={APP_HEADER.MOCK_USER}
                          size="small"
                          sx={{ marginX: '1px' }}
                          onClick={() => {
                            window.location.href = '/internal/mock-user';
                          }}
                          color="inherit"
                          icon={<RepeatOnRounded />}
                        />

                        <IconButton
                          title={APP_HEADER.SUPER_ADMIN}
                          size="small"
                          sx={{ marginX: '1px' }}
                          onClick={() => {
                            window.location.href = '/internal/super-admin';
                          }}
                          color="inherit"
                          icon={<PersonPinRounded />}
                        />
                      </>
                    )}
                    {isStaff && !isStaffViewApplicant && (
                      <Box>
                        <IconButton
                          size="small"
                          sx={{ marginLeft: '14px' }}
                          title={APP_HEADER.SWITCH_ROLE}
                          aria-label="account of current user"
                          aria-controls="menu-appbar"
                          aria-haspopup="true"
                          onClick={handleOpenRoleMenu}
                          color="inherit"
                          icon={<SyncAltOutlined />}
                        />

                        <RoleSwitchBar
                          open={Boolean(anchorElRole)}
                          anchorEl={anchorElRole}
                          handleClose={handleCloseRoleMenu}
                        />

                        {showDelegation && (
                          <>
                            <IconButton
                              size="small"
                              title={APP_HEADER.DELEGATION}
                              onClick={handleOpenDelegation}
                              color="inherit"
                              aria-controls={
                                openDelegationList ? 'basic-menu' : undefined
                              }
                              aria-haspopup="true"
                              aria-expanded={
                                openDelegationList ? 'true' : undefined
                              }
                              icon={<RecentActors />}
                            />

                            <Menu
                              id="basic-menu"
                              anchorEl={anchorEl}
                              open={openDelegationList}
                              onClose={handleCloseDelegation}
                              MenuListProps={{
                                'aria-labelledby': 'basic-button',
                              }}
                            >
                              {delegation.map((delegator, index) => {
                                return (
                                  <MenuItem
                                    key={index}
                                    onClick={handleToDelegate(
                                      delegator.netId,
                                      delegator?.department ?? '',
                                    )}
                                  >
                                    {delegator.netId}
                                    {' - Supervisor'}{' '}
                                    {/* delegation function is only open to supervisors  */}
                                  </MenuItem>
                                );
                              })}
                            </Menu>
                          </>
                        )}
                      </Box>
                    )}

                    {/* hide log out when staff view as applicant */}
                    {!isStaffViewApplicant && (
                      <IconButton
                        title={APP_HEADER.LOG_OUT}
                        size="small"
                        sx={{ marginX: '1px' }}
                        onClick={() => {
                          setDialogDetails(
                            dialogMappings[DIALOG_TEXT.LOGOUT.KEY],
                          );
                          setDialogOpen(true);
                        }}
                        color="inherit"
                        icon={<LogoutOutlined />}
                      />
                    )}
                  </Box>

                  {/* delegation area */}
                  {delegateMode && (
                    <Box className="flex justify-end">
                      <DelegatePanel />

                      <IconButton
                        title={APP_HEADER.EXIT_DELEGATION}
                        size="small"
                        sx={{ marginX: '1px', color: 'black' }}
                        onClick={() => {
                          setDialogDetails(
                            dialogMappings[DIALOG_TEXT.EXIT_DELEGATION.KEY],
                          );
                          setDialogOpen(true);
                        }}
                        icon={<ExitToApp />}
                      />
                    </Box>
                  )}
                </div>
              )}
            </div>
          </Toolbar>
        </Container>
      </AppBar>
    </div>
  );
};
